import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ConfigProvider, App as AntApp } from "antd";
import { setAntdStatic } from "./utils/antdStatic";
import esES from "antd/locale/es_ES";
import { antdTheme } from "./theme/tokens";
import "./theme/variables.css";
import "./Css/shared-page.css";
import Login from "./Pages/login/login";
import PassRecovery from "./Pages/login/recovery";
import Home from "./Pages/home/home";
import AseosPage from "./Pages/aseos/aseos";
import PasswordReset from "./Pages/login/passwordReset";
import ClientesPage from "./Pages/clientes/clientes";
import Perfil from "./Pages/perfil/perfil";
import FinanzasAseadorAdmin from "./Pages/finanzas-aseador-admin/finanzasAseadorAdmin";
import FinanzasTrabajadorAdmin from "./Pages/finanzas-trabajador-admin/finanzasTrabajadorAdmin";
import Historial from "./Pages/historial/historial";
import ActividadClientes from "./Pages/actividadClientes/actividadClientes";
import Almacenamiento from "./Pages/almacenamiento/almacenamiento";
import FinanzasClientes from "./Pages/finanzas-clientes/finanzasClientes";
import FinanzasEmpresa from "./Pages/finanzas-empresa/finanzasEmpresa";
import FlujosPage from "./Pages/flujos/Flujos";
import SueldosPage from "./Pages/sueldos/sueldos";
import ArriendosPage from "./Pages/arriendos/arriendos";
import TrabajadoresPage from "./Pages/trabajadores/trabajadores";
import AseadoresPage from "./Pages/aseadores/aseadores";
import AgendaMaestrosPage from "./Pages/agendaMaestros/agendaMaestros";
import CostosSugeridosPage from "./Pages/costosSugeridos/costosSugeridos";
import MercadoClientePage from "./Pages/mercado/MercadoClientePage";
import Propiedades from "./Pages/propiedades/propiedades";
import TasksPage from "./Pages/tareas/tareas";
import TareasCliente from "./Pages/tareas/tareasCliente";
import TareasAseador from "./Pages/tareas/tareasAseador";
import FinanzasAseador from "./Pages/finanzas-aseador/finanzasAseador";
import CalendarioAseador from "./Pages/calendario-aseador/calendarioAseador";
import Calendario from "./Pages/calendario/calendario";
import { Inventario } from "./Pages/inventario/inventario";
import LavanderiaPage from "./Pages/lavanderia/lavanderia";
import BackupPage from "./Pages/backup/BackupPage";
import DocumentViewerPage from "./Pages/DocumentViewer/DocumentViewerPage";
import DocumentosPage from "./Pages/Contratos/ContratosPage";
import AdministradoresPage from "./Pages/administradores/administradores";
import StaysWebhooksPage from "./Pages/stays-webhooks/stays-webhooks";
import AlertasPage from "./Pages/alertas/AlertasPage";
import NotFound from "./Pages/not-found/NotFound";
import ProtectedRoute from "./Components/ProtectedRoute";
import ChunkErrorBoundary from "./Components/ChunkErrorBoundary";
import ClientActivityTracker from "./Components/ClientActivityTracker";
import InstalarAppAviso from "./Components/InstalarAppAviso";
import { useAuth } from "./context/AuthContext";
import { useDocumentTitle } from "./hooks/useDocumentTitle";
import { loadIvaRate } from "./utils/iva";

// Lazy load — round F2 2026-04-29. Estas dos páginas usan chart.js +
// react-chartjs-2 (~155 KB) para los dashboards. Solo se renderizan
// en /estadisticas y /estadisticas-admin, no necesitan estar en main.
const ClientStatsPage = React.lazy(
  () => import("./Pages/estadisticas/ClientStatsPage"),
);
const AdminStatsPage = React.lazy(
  () => import("./Pages/estadisticas/AdminStatsPage"),
);
// Mercado admin también usa chart.js (tab Análisis) → lazy por el mismo
// motivo. La vista cliente (/mercado-cliente) NO usa chart.js y va estática
// (import arriba). Round 2026-07-21 — ver docs/modulo-mercado.md.
const MercadoAdminPage = React.lazy(
  () => import("./Pages/mercado/MercadoAdminPage"),
);

/** Redirects to /home (o /aseos si es aseador) si está autenticado, o /login si no. */
const RootRedirect: React.FC = () => {
  const { token, loading, user } = useAuth();
  if (loading) return null;
  if (!token) return <Navigate to="/login" replace />;
  const rol = String(user?.rol ?? "").toLowerCase();
  return <Navigate to={rol === "aseador" ? "/aseos" : "/home"} replace />;
};

function AppRoutes() {
  useDocumentTitle();
  const { token } = useAuth();

  // Cargar la tasa de IVA configurable (default 19% mientras no responda /
  // si falla). Los formularios que convierten neto↔IVA (propiedades,
  // lavandería, calendario) leen el valor SYNC desde utils/iva.ts.
  // Además de la carga al autenticarse, re-chequeamos al VOLVER el foco a
  // la pestaña (con TTL interno de 5 min — barato): una pestaña abierta
  // hace días no debe quedarse con una tasa vieja si otro admin la cambió,
  // porque el modal del calendario persiste los IVA que calcula el cliente.
  // Ver flujo-financiero.md §8.0.
  useEffect(() => {
    if (!token) return;
    void loadIvaRate();
    const onVisible = () => {
      if (document.visibilityState === "visible") void loadIvaRate();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [token]);

  return (
    <>
    {/* Beacon de navegación de clientes (no renderiza nada). Registra el
        cambio de página en el historial de actividad. Ver ClientActivityTracker. */}
    <ClientActivityTracker />
    {/* Invitación a instalar la app en el celular (hoja inferior; se apaga sola en /login y en escritorio). Ver InstalarAppAviso. */}
    <InstalarAppAviso />
    <Routes>
          {/* ── Public routes ───────────────────────────────────────────── */}
          <Route path="/login" element={<Login />} />
          <Route path="/recuperar-contrasena" element={<PassRecovery />} />
          <Route
            path="/recuperar-contrasena/:token"
            element={<PasswordReset />}
          />

          {/* ── Protected routes ─────────────────────────────────────────── */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <Perfil />
              </ProtectedRoute>
            }
          />
          <Route
            path="/estadisticas"
            element={
              <ProtectedRoute>
                <ChunkErrorBoundary>
                  <Suspense fallback={null}>
                    <ClientStatsPage />
                  </Suspense>
                </ChunkErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/estadisticas-admin"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <ChunkErrorBoundary>
                  <Suspense fallback={null}>
                    <AdminStatsPage />
                  </Suspense>
                </ChunkErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/aseos"
            element={
              <ProtectedRoute allowedRoles={["administrador", "aseador"]}>
                <AseosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes-propietarios"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <ClientesPage />
              </ProtectedRoute>
            }
          />
          {/* /clientes queda como alias legacy → /clientes-propietarios.
              La sección lista clientes Y propietarios; el nombre nuevo lo
              hace explícito. Redirect para no romper favoritos. Round
              2026-06-23. */}
          <Route
            path="/clientes"
            element={<Navigate to="/clientes-propietarios" replace />}
          />
          {/* Vista admin/superadmin del módulo "Finanzas del aseador" —
              examinar el panel que ve cada aseador + global. Reemplaza la
              página legacy `/finanzas` (eliminada en Round 2026-04-29).
              Ver docs/modulo-finanzas-aseador.md. */}
          <Route
            path="/finanzas-aseador-admin"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <FinanzasAseadorAdmin />
              </ProtectedRoute>
            }
          />
          {/* Panel admin del ledger del trabajador (rol sin login). Espejo del
              panel del aseador pero movimientos-only y sin auto-vista. Cierra el
              ciclo de aprobación del reembolso de inventario al trabajador
              (tipo=4). Ver docs/modulo-finanzas-aseador.md §11. */}
          <Route
            path="/finanzas-trabajador-admin"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <FinanzasTrabajadorAdmin />
              </ProtectedRoute>
            }
          />
          {/* Path legacy `/finanzas`: pasaba `user.id` del admin al endpoint
              `/api/finanzas/:id` que solo busca aseos donde id_usuario=admin
              → siempre vacío. Redirect al nuevo path para no romper bookmarks. */}
          <Route
            path="/finanzas"
            element={<Navigate to="/finanzas-aseador-admin" replace />}
          />
          <Route
            path="/finanzas-cliente"
            element={
              <ProtectedRoute>
                <FinanzasClientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventario"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <Inventario />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lavanderia"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <LavanderiaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/historial"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <Historial />
              </ProtectedRoute>
            }
          />
          <Route
            path="/actividad-clientes"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <ActividadClientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/almacenamiento"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <Almacenamiento />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finanzas-empresa"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <FinanzasEmpresa />
              </ProtectedRoute>
            }
          />
          {/*
            Rutas dedicadas para CRUD: /finanzas-empresa pasa a ser puro
            reporte. La creación/edición/eliminación de cada tipo de
            movimiento vive en su propia página bajo GESTIÓN.
          */}
          {/* Round 2026-05-13 — /gastos renombrado a /flujos para reflejar
              que el módulo cubre los 3 sentidos del flujo de caja (gasto,
              ingreso, intermediario), no solo egresos. /gastos se mantiene
              como alias legacy para no romper bookmarks ni links externos:
              renderiza la misma página (GastosPage = wrapper que termina
              redirigiendo a FlujosPage en el menú principal). */}
          <Route
            path="/flujos"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <FlujosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/gastos"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <FlujosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sueldos"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <SueldosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/arriendos"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <ArriendosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trabajadores"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <TrabajadoresPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aseadores"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <AseadoresPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agenda-contactos"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <AgendaMaestrosPage />
              </ProtectedRoute>
            }
          />
          {/* Alias legacy del renombre "Agenda de maestros" → "Agenda de
              contactos" (2026-07-02). Bookmarks viejos siguen funcionando. */}
          <Route
            path="/agenda-maestros"
            element={<Navigate to="/agenda-contactos" replace />}
          />
          <Route
            path="/costos-sugeridos"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <CostosSugeridosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mercado"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <ChunkErrorBoundary>
                  <Suspense fallback={null}>
                    <MercadoAdminPage />
                  </Suspense>
                </ChunkErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mercado-cliente"
            element={
              <ProtectedRoute>
                <MercadoClientePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/propiedades"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <Propiedades />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tareas"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <TasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tareas-cliente"
            element={
              <ProtectedRoute>
                <TareasCliente />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tareas-aseador"
            element={
              <ProtectedRoute allowedRoles={["aseador"]}>
                <TareasAseador />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finanzas-aseador"
            element={
              <ProtectedRoute allowedRoles={["aseador"]}>
                <FinanzasAseador />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendario-aseador"
            element={
              <ProtectedRoute allowedRoles={["aseador"]}>
                <CalendarioAseador />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendario"
            element={
              <ProtectedRoute>
                <Calendario />
              </ProtectedRoute>
            }
          />

          <Route
            path="/documentos"
            element={
              <ProtectedRoute>
                <DocumentosPage />
              </ProtectedRoute>
            }
          />
          {/* /contratos queda como alias legacy que redirige a /documentos,
              para no romper bookmarks ni enlaces históricos. */}
          <Route
            path="/contratos"
            element={<Navigate to="/documentos" replace />}
          />

          {/*
            Ruta canónica: /configuracion (round 2026-04-30 decies — el
            usuario reportó que `/administradores` no reflejaba el
            contenido real, que es "Configuración del sistema" con
            varias pestañas: Administradores, Opciones generales,
            Zonas geográficas, Optimización imágenes/videos).
            Mantenemos `/administradores` como alias por compat con
            bookmarks/links viejos — apunta al mismo componente.
            La página interna ya tiene "Configuración" como header y
            permisos de admin/superadmin (la pestaña "Administradores"
            es solo-superadmin via `isSuperAdmin` interno).
          */}
          <Route
            path="/configuracion"
            element={
              <ProtectedRoute allowedRoles={["administrador"]}>
                <AdministradoresPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/administradores"
            element={<Navigate to="/configuracion" replace />}
          />

          <Route
            path="/backup"
            element={
              <ProtectedRoute allowedRoles={["superadministrador"]}>
                <BackupPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/stays-webhooks"
            element={
              <ProtectedRoute allowedRoles={["superadministrador", "administrador"]}>
                <StaysWebhooksPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/alertas"
            element={
              <ProtectedRoute allowedRoles={["superadministrador", "administrador"]}>
                <AlertasPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/visor/:source/:id/:fileIndex"
            element={
              <ProtectedRoute>
                <DocumentViewerPage />
              </ProtectedRoute>
            }
          />

          {/* ── Root redirect ─────────────────────────────────────────────── */}
          <Route path="/" element={<RootRedirect />} />

          {/* ── Catch-all 404 ─────────────────────────────────────────────── */}
          {/* Cualquier ruta no declarada arriba. Genérica y sin fuga de info
              (ver Pages/not-found/NotFound.tsx). Fuera de ProtectedRoute para
              que sirva a usuarios autenticados y anónimos por igual. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
    </>
  );
}

/** Captura las instancias de message/modal/notification del contexto de AntApp */
const AntdStaticBridge: React.FC = () => {
  const { message, modal, notification } = AntApp.useApp();
  React.useEffect(() => {
    setAntdStatic({ message, modal, notification });
  }, [message, modal, notification]);
  return null;
};

const App: React.FC = () => {
  return (
    <Router>
      <ConfigProvider locale={esES} theme={antdTheme}>
        <AntApp
          message={{ maxCount: 3, duration: 4 }}
          style={{ fontSize: 15 }}
        >
          <AntdStaticBridge />
          <AppRoutes />
        </AntApp>
      </ConfigProvider>
    </Router>
  );
};

export default App;
